import { REDO_LOCAL_STORAGE_KEY_OPEN } from "@redotech/ai-autopilot-constants/constant";
import { ReactNode, createContext, memo, useCallback, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { WidgetView } from "../core/routes";

export interface WidgetNavigationContextType {
  history: NavigationEntry[];
  navigate: ({
    to,
    orderId,
  }: {
    to: WidgetView;
    orderId?: string | undefined;
  }) => void;
  goBack: () => void;
  replaceState: (withView: WidgetView, orderId?: string) => void;
  currentView: WidgetView;
  currentOrderId?: string;
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

interface NavigationEntry {
  view: WidgetView;
  orderId?: string;
}

export const WidgetNavigationContext = createContext<
  WidgetNavigationContextType | undefined
>(undefined);

export const WidgetNavigationProvider = memo(function WidgetNavigationProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const [history, setHistory] = useState<NavigationEntry[]>([
    { view: WidgetView.HOME },
  ]);
  const [isOpen, setIsOpen] = useLocalStorage<boolean>(
    REDO_LOCAL_STORAGE_KEY_OPEN,
    false,
  );

  const currentEntry = history[history.length - 1];
  const currentView = currentEntry.view;
  const currentOrderId = currentEntry.orderId;

  const navigate = useCallback(
    ({
      to,
      orderId = undefined,
      replace = false,
    }: {
      to: WidgetView;
      orderId?: string | undefined;
      replace?: boolean;
    }) => {
      const newEntry: NavigationEntry = { view: to, orderId };

      // don't navigate if we're already on the same view and order ID
      if (to === currentView && orderId === currentOrderId) {
        return;
      }

      if (replace) {
        setHistory((prev) => [...prev.slice(0, -1), newEntry]);
      } else {
        setHistory((prev) => [...prev, newEntry]);
      }
    },
    [currentView, currentOrderId],
  );

  const goBack = useCallback(() => {
    setHistory((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const replaceState = useCallback((withView: WidgetView, orderId?: string) => {
    const newEntry: NavigationEntry = { view: withView, orderId };
    setHistory((prev) => [...prev.slice(0, -1), newEntry]);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return (
    <WidgetNavigationContext.Provider
      value={{
        history,
        navigate,
        goBack,
        currentView,
        currentOrderId,
        replaceState,
        isOpen,
        open,
        close,
      }}
    >
      {children}
    </WidgetNavigationContext.Provider>
  );
});
